import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { getApiUrl } from './config'
import { string } from 'zod'

// Invoice PDF Generator - All colors set to black (no teal)
// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable
  }
}

interface InvoicePDFOptions {
  invoice: {
    id: string
    invoiceNumber: string
    issueDate: string
    dueDate?: string
    status: string
    totalAmount: number
    balanceDue: number
    currency: string
    subtotal?: number
    taxAmount?: number
    discountAmount?: number
    customer?: {
      name: string
      email?: string
      address?: string
      phone?: string
      taxId?: string
    }
    lines?: Array<{
      description: string
      quantity: number
      unitPrice: number
      lineTotal: number
      taxRate?: number
      discountRate?: number
      discountAmount?: number
      taxAmount?: number
    }>
    notes?: string
    paymentUrl?: string
  }
  company: {
    name: string
    logoUrl?: string
    primaryColor?: string
    address?: string
    city?: string
    state?: string
    postalCode?: string
    email?: string
    phone?: string
    website?: string
    taxId?: string
    invoiceTerms?: string
    invoiceFooter?: string
  }
}

export class InvoicePDFGenerator {
  private doc: jsPDF
  private primaryColor: string
  private margin = 20
  private currentY = 20

  constructor(private options: InvoicePDFOptions) {
    this.doc = new jsPDF('p', 'mm', 'a4')
    // Always use black color for invoices instead of company's primaryColor
    this.primaryColor = '#000000'
  }

  private formatCurrency(amount: number, currency: string): string {
    // Always use Frw format for Rwandan Francs
    if (currency === 'Frw' || currency === 'FRW' || currency === 'RWF') {
      return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw`
    }
    // For other currencies, try standard formatting
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount)
    } catch {
      return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
    }
  }

  private setColor(color: string) {
    // Convert hex to RGB
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    this.doc.setTextColor(r, g, b)
  }

  private resetColor() {
    this.doc.setTextColor(0, 0, 0)
  }

  private addLine(y?: number) {
    const lineY = y || this.currentY
    this.doc.setDrawColor(200, 200, 200)
    this.doc.line(this.margin, lineY, 190, lineY)
  }

  private generateBarcode(): string {
    // Generate scannable Code39 barcode with invoice information
    const canvas = document.createElement('canvas')
    canvas.width = 150
    canvas.height = 40
    const ctx = canvas.getContext('2d')
    
    if (!ctx) return ''
    
    // White background
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    // Create barcode data - use invoice number as primary identifier
    // Remove "INV-" or "POS-" prefix if present, keep only the numeric/alphanumeric part
    let barcodeData = (this.options.invoice.invoiceNumber || 'INV001').toUpperCase()
    // Remove "INV-" or "POS-" prefix if it exists
    barcodeData = barcodeData.replace(/^(INV|POS)-?/i, '')
    // Remove any characters not supported by Code39
    barcodeData = barcodeData.replace(/[^0-9A-Z\s\-\.\$\/\+\%]/g, '')
    // Limit length to fit on canvas
    if (barcodeData.length > 15) {
      barcodeData = barcodeData.substring(0, 15)
    }
    
    // Code39 character encoding (each character = 9 elements: 5 bars, 4 spaces)
    const code39Pattern: { [key: string]: string } = {
      '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
      '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
      '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', 'A': 'wnnnnwnnw', 'B': 'nnwnnwnnw',
      'C': 'wnwnnwnnn', 'D': 'nnnnwwnnw', 'E': 'wnnnwwnnn', 'F': 'nnwnwwnnn',
      'G': 'nnnnnwwnw', 'H': 'wnnnnwwnn', 'I': 'nnwnnwwnn', 'J': 'nnnnwwwnn',
      'K': 'wnnnnnnww', 'L': 'nnwnnnnww', 'M': 'wnwnnnnwn', 'N': 'nnnnwnnww',
      'O': 'wnnnwnnwn', 'P': 'nnwnwnnwn', 'Q': 'nnnnnnwww', 'R': 'wnnnnnwwn',
      'S': 'nnwnnnwwn', 'T': 'nnnnwnwwn', 'U': 'wwnnnnnnw', 'V': 'nwwnnnnnw',
      'W': 'wwwnnnnnn', 'X': 'nwnnwnnnw', 'Y': 'wwnnwnnnn', 'Z': 'nwwnwnnnn',
      '-': 'nwnnnnwnw', '.': 'wwnnnnwnn', ' ': 'nwwnnnwnn', '$': 'nwnwnwnnn',
      '/': 'nwnnnwnwn', '+': 'nwnwnnnwn', '%': 'nnnwnwnwn'
    }
    
    const barHeight = 30
    const narrowBar = 1.5  // Narrow bar/space width
    const wideBar = 3.5    // Wide bar/space width (2.3x narrow)
    let currentX = 10
    
    // Helper function to render a Code39 pattern (alternates bars and spaces)
    const renderPattern = (pattern: string, x: number): number => {
      let pos = x
      for (let i = 0; i < pattern.length; i++) {
        const isWide = pattern[i] === 'w'
        const width = isWide ? wideBar : narrowBar
        
        if (i % 2 === 0) {
          // Even positions are bars - draw them
          ctx.fillStyle = '#000000' // Always use black for barcode
          ctx.fillRect(pos, 5, width, barHeight)
        }
        // Odd positions are spaces - just advance position (already white background)
        pos += width
      }
      return pos
    }
    
    // Start character (*) - pattern: nwnnwnwnn
    const startPattern = 'nwnnwnwnn'
    currentX = renderPattern(startPattern, currentX)
    
    // Character gap (inter-character space)
    currentX += narrowBar
    
    // Encode each character
    for (let charIndex = 0; charIndex < barcodeData.length && currentX < canvas.width - 20; charIndex++) {
      const char = barcodeData[charIndex]
      const pattern = code39Pattern[char] || code39Pattern['0']
      
      currentX = renderPattern(pattern, currentX)
      
      // Character gap (inter-character space)
      if (charIndex < barcodeData.length - 1) {
        currentX += narrowBar
      }
    }
    
    // Stop character (*)
    currentX += narrowBar // Gap before stop character
    renderPattern(startPattern, currentX)
    
    // Display barcode text below for human readability
    ctx.fillStyle = '#000000'
    ctx.font = '8px Arial'
    ctx.textAlign = 'center'
    ctx.fillText(barcodeData, canvas.width / 2, canvas.height - 2)
    
    return canvas.toDataURL()
  }

  private async addHeader() {
    const { company, invoice } = this.options
    
    // Reset to black color at the start
    this.doc.setTextColor(0, 0, 0)
    
    // Professional logo and company name (same style as journal entry)
    let logoAdded = false;
    const logoSize = 18;
    const logoY = this.currentY;

    if (company.logoUrl) {
      try {
        const logoUrl = company.logoUrl.toLowerCase();

        if (logoUrl.includes('.jpg') || logoUrl.includes('.jpeg')) {
          this.doc.addImage(company.logoUrl, 'JPEG', this.margin, logoY, logoSize, logoSize)
          logoAdded = true;
        } else if (logoUrl.includes('.png')) {
          this.doc.addImage(company.logoUrl, 'PNG', this.margin, logoY, logoSize, logoSize)
          logoAdded = true;
        } else {
          try {
            this.doc.addImage(company.logoUrl, 'PNG', this.margin, logoY, logoSize, logoSize)
            logoAdded = true;
          } catch (pngError) {
            try {
              this.doc.addImage(company.logoUrl, 'JPEG', this.margin, logoY, logoSize, logoSize)
              logoAdded = true;
            } catch (jpegError) {
              // Continue without logo
            }
          }
        }
      } catch (error) {
        logoAdded = false;
      }
    }

    // Company name aligned with logo center
    const companyNameY = logoY + (logoSize / 2) + 2;
    const companyX = logoAdded ? this.margin + logoSize + 2 : this.margin;
    
    this.doc.setFontSize(18)
    this.doc.setTextColor(0, 0, 0)
    this.doc.setFont(undefined, 'bold')
    this.doc.text(company.name, companyX, companyNameY)
    this.doc.setFont(undefined, 'normal')
    
    // Company website below name
    let detailsY = logoY + logoSize + 8
    if (company.website) {
      this.doc.setFontSize(9)
      this.doc.setTextColor(100, 100, 100)
      this.doc.text(company.website, companyX, companyNameY + 5)
      detailsY = companyNameY + 10
    }

    // Company details (keep existing layout)
    this.resetColor()
    this.doc.setFontSize(10)
    
    if (company.address) {
      this.doc.text(company.address, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }
    
    if (company.city || company.state || company.postalCode) {
      const location = [company.city, company.state, company.postalCode].filter(Boolean).join(', ')
      this.doc.text(location, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }
    
    if (company.email) {
      this.doc.text(`Email: ${company.email}`, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }
    
    if (company.phone) {
      this.doc.text(`Phone: ${company.phone}`, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }

    // Invoice header on the right (aligned with logo)
    this.doc.setFontSize(28)
    this.doc.setTextColor(0, 0, 0)
    this.doc.text('INVOICE', 190 - this.doc.getTextWidth('INVOICE'), logoY + 10)
    
    this.resetColor()
    this.doc.setFontSize(16)
    // Calculate invoice number position - use this as reference for barcode alignment
    const invoiceNumberText = `#${invoice.invoiceNumber}`
    const invoiceNumberX = 190 - this.doc.getTextWidth(invoiceNumberText)
    const invoiceNumberY = logoY + 20
    this.doc.text(invoiceNumberText, invoiceNumberX, invoiceNumberY)
    
    // Status badge (aligned with invoice header)
    this.doc.setFontSize(10)
    const statusText = invoice.status.toUpperCase()
    const statusWidth = this.doc.getTextWidth(statusText) + 4
    
    // Status background
    this.doc.setFillColor(240, 240, 240)
    this.doc.rect(190 - statusWidth, logoY + 25, statusWidth, 6, 'F')
    
    this.doc.text(statusText, 190 - statusWidth + 2, logoY + 29)

    // Add Barcode - aligned with the same left margin as invoice number
    try {
      const barcode = this.generateBarcode()
      if (barcode) {
        // Align barcode with invoice number (same left margin)
        // Barcode width is approximately 60mm, positioned at invoice number X
        const barcodeWidth = 60 // mm
        const barcodeHeight = 8 // mm
        const barcodeY = logoY + 35 // Position below invoice number
        this.doc.addImage(barcode, 'PNG', invoiceNumberX, barcodeY, barcodeWidth, barcodeHeight)
      }
    } catch (error) {
      // Continue without barcode
    }

    this.currentY = Math.max(detailsY, logoY + 60)
    this.addLine(this.currentY)
    this.currentY += 10
  }

  private addInvoiceDetails() {
    const { invoice } = this.options
    
    // Bill To section
    this.doc.setFontSize(12)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setTextColor(0, 0, 0) // Black color
    this.doc.text('BILL TO:', this.margin, this.currentY)
    
    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(10)
    this.doc.setTextColor(0, 0, 0) // Black color
    let billToY = this.currentY + 6
    
    if (invoice.customer?.name) {
      this.doc.setFont('helvetica', 'bold')
      this.doc.text(invoice.customer.name, this.margin, billToY)
      this.doc.setFont('helvetica', 'normal')
      billToY += 5
    } else {
      // Show "Unknown Customer" when no customer is provided
      this.doc.setFont('helvetica', 'bold')
      this.doc.text('Unknown Customer', this.margin, billToY)
      this.doc.setFont('helvetica', 'normal')
      billToY += 5
    }
    
    if (invoice.customer?.address) {
      this.doc.text(invoice.customer.address, this.margin, billToY)
      billToY += 4
    }
    
    if (invoice.customer?.email) {
      this.doc.text(invoice.customer.email, this.margin, billToY)
      billToY += 4
    }
    
    if (invoice.customer?.phone) {
      this.doc.text(invoice.customer.phone, this.margin, billToY)
      billToY += 4
    }

    // Invoice details on the right - moved further right to avoid collision
    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(12)
    this.doc.setTextColor(0, 0, 0) // Black color
    this.doc.text('INVOICE DETAILS', 120, this.currentY)
    
    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(10)
    this.doc.setTextColor(0, 0, 0) // Black color
    let detailsY = this.currentY + 6
    
    const details = [
      ['Issue Date:', new Date(invoice.issueDate).toLocaleDateString()],
      ...(invoice.dueDate ? [['Due Date:', new Date(invoice.dueDate).toLocaleDateString()]] : []),
      ['Currency:', invoice.currency],
      ['Amount:', this.formatCurrency(invoice.totalAmount, invoice.currency)],
      ...(invoice.balanceDue > 0 ? [['Balance Due:', this.formatCurrency(invoice.balanceDue, invoice.currency)]] : [])
    ]
    
    details.forEach(([label, value]) => {
      this.doc.text(label, 120, detailsY)
      this.doc.text(value, 155, detailsY)
      detailsY += 4
    })

    this.currentY = Math.max(billToY, detailsY) + 10
  }

  private addLineItems() {
    const { invoice } = this.options
    
    if (!invoice.lines || invoice.lines.length === 0) return

    // Table headers - always include Tax column
    const headers = [['Description', 'Qty', 'Rate', 'Tax', 'Amount']]

    // Table data
    const data = invoice.lines.map(line => {
      // Calculate line total: quantity * unitPrice (use stored lineTotal if > 0, otherwise calculate)
      const calculatedTotal = Number(line.quantity) * Number(line.unitPrice)
      const lineTotal = (Number(line.lineTotal) || 0) > 0 ? Number(line.lineTotal) : calculatedTotal
      
      // Tax column
      const taxRate = Number(line.taxRate) || 0
      const taxAmount = Number(line.taxAmount) || 0
      const taxText = taxRate > 0 
        ? `${taxRate.toFixed(2)}%` 
        : taxAmount > 0 
          ? this.formatCurrency(taxAmount, invoice.currency)
          : '-'
      
      const row = [
        line.description,
        Number(line.quantity).toFixed(0),
        this.formatCurrency(Number(line.unitPrice), invoice.currency),
        taxText,
        this.formatCurrency(lineTotal, invoice.currency)
      ]
      
      return row
    })

    // Generate table with proper column widths and consistent padding
    autoTable(this.doc, {
      head: headers,
      body: data,
      startY: this.currentY,
      theme: 'striped',
      margin: { left: this.margin, right: this.margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        valign: 'middle',
        halign: 'left',
        lineColor: [220, 220, 220],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        cellPadding: 3,
        valign: 'middle',
        halign: 'left'
      },
      bodyStyles: {
        cellPadding: 3,
        valign: 'middle',
        halign: 'left'
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 28, halign: 'center' },
        4: { cellWidth: 35, halign: 'right' }
      }
    })

    this.currentY = (this.doc as any).lastAutoTable.finalY + 10
  }

  private addTotals() {
    const { invoice } = this.options
    
    // Calculate subtotal from line items (before discounts and tax)
    const subtotal = invoice.subtotal || invoice.lines?.reduce((sum, line) => {
      const lineSubtotal = Number(line.quantity) * Number(line.unitPrice)
      return sum + lineSubtotal
    }, 0) || 0
    
    // Ensure subtotal is a proper number with 2 decimals
    const formattedSubtotal = Number(Number(subtotal).toFixed(2))
    
    // Calculate total discount (invoice-level or from line items)
    let discount = invoice.discountAmount || 0
    if (discount === 0 && invoice.lines) {
      discount = invoice.lines.reduce((sum, line) => {
        const discountRate = Number(line.discountRate) || 0
        const discountAmount = Number(line.discountAmount) || 0
        
        if (discountAmount > 0) {
          return sum + discountAmount
        } else if (discountRate > 0) {
          const lineSubtotal = Number(line.quantity) * Number(line.unitPrice)
          return sum + (lineSubtotal * (discountRate / 100))
        }
        return sum
      }, 0)
    }
    
    // Calculate total tax (invoice-level or from line items)
    let tax = invoice.taxAmount || 0
    if (tax === 0 && invoice.lines) {
      tax = invoice.lines.reduce((sum, line) => {
        const taxRate = Number(line.taxRate) || 0
        const taxAmount = Number(line.taxAmount) || 0
        
        if (taxAmount > 0) {
          return sum + taxAmount
        } else if (taxRate > 0) {
          const lineSubtotal = Number(line.quantity) * Number(line.unitPrice)
          const lineDiscount = Number(line.discountAmount) || (lineSubtotal * (Number(line.discountRate) || 0) / 100)
          const taxableAmount = lineSubtotal - lineDiscount
          return sum + (taxableAmount * (taxRate / 100))
        }
        return sum
      }, 0)
    }
    
    const totalsX = 130
    const labelX = 110
    
    // Subtotal
    this.doc.setFontSize(10)
    this.doc.text('Subtotal:', labelX, this.currentY)
    this.doc.text(this.formatCurrency(formattedSubtotal, invoice.currency), totalsX, this.currentY)
    this.currentY += 5
    
    // Discount - always show
    this.doc.text('Discount:', labelX, this.currentY)
    if (discount > 0) {
      this.doc.text(`-${this.formatCurrency(discount, invoice.currency)}`, totalsX, this.currentY)
    } else {
      this.doc.text('-', totalsX, this.currentY)
    }
    this.currentY += 5
    
    // Tax - always show
    this.doc.text('Tax:', labelX, this.currentY)
    if (tax > 0) {
      this.doc.text(this.formatCurrency(tax, invoice.currency), totalsX, this.currentY)
    } else {
      this.doc.text('-', totalsX, this.currentY)
    }
    this.currentY += 5
    
    // Line above total
    this.doc.setDrawColor(100, 100, 100)
    this.doc.line(labelX, this.currentY + 2, 185, this.currentY + 2)
    this.currentY += 8
    
    // Total
    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(12)
    this.doc.setTextColor(0, 0, 0)
    this.doc.text('TOTAL:', labelX, this.currentY)
    this.doc.text(this.formatCurrency(invoice.totalAmount, invoice.currency), totalsX, this.currentY)
    
    this.resetColor()
    this.currentY += 10
  }

  private addNotes() {
    const { invoice } = this.options
    
    if (invoice.notes) {
      this.currentY += 5
      this.doc.setFont('helvetica', 'bold')
      this.doc.setFontSize(11)
      this.doc.text('NOTES:', this.margin, this.currentY)
      
      this.doc.setFont('helvetica', 'normal')
      this.doc.setFontSize(10)
      this.currentY += 6
      
      const lines = this.doc.splitTextToSize(invoice.notes, 170)
      this.doc.text(lines, this.margin, this.currentY)
      this.currentY += lines.length * 4 + 5
    }
  }

  private addFooter() {
    const { company } = this.options
    
    // Footer text (removed payment terms section)
    if (company.invoiceFooter) {
      this.currentY += 10
      this.doc.setFontSize(8)
      this.doc.setTextColor(100, 100, 100)
      const footerLines = this.doc.splitTextToSize(company.invoiceFooter, 170)
      this.doc.text(footerLines, this.margin, this.currentY)
      this.currentY += footerLines.length * 3
    }
    
    // Generation timestamp
    this.currentY += 5
    this.doc.setFontSize(7)
    this.doc.setTextColor(150, 150, 150)
    const timestamp = `Generated on ${new Date().toLocaleString()} • Invoice #${this.options.invoice.invoiceNumber} • Created via Ikoro`
    this.doc.text(timestamp, 105 - this.doc.getTextWidth(timestamp) / 2, this.currentY)
  }

  public async generate(): Promise<Blob> {
    await this.addHeader()
    this.addInvoiceDetails()
    this.addLineItems()
    this.addTotals()
    this.addNotes()
    this.addFooter()
    
    return this.doc.output('blob')
  }

  public async download(filename?: string): Promise<void> {
    await this.addHeader()
    this.addInvoiceDetails()
    this.addLineItems()
    this.addTotals()
    this.addNotes()
    this.addFooter()
    
    const defaultFilename = `invoice-${this.options.invoice.invoiceNumber}.pdf`
    this.doc.save(filename || defaultFilename)
  }
}
